/**
 * navDestinations — CHROME-1.  One list of nav destinations, read by
 * BOTH chromes.
 *
 * WHY this file exists: the six-destination structure NAV-6 shipped
 * was declared twice — `SIMPLIFIED_DESTINATIONS` in AppApertureBar
 * and `simplifiedGroups` in AppSidebar.  Two copies of the same
 * truth means the day someone adds a seventh destination they update
 * one and ship a nav that disagrees with itself depending on which
 * chrome you picked.  CHROME-1 also lets studios choose *which*
 * destinations they keep and in what order, which makes a single
 * source of truth non-negotiable: the preference stores ids, and ids
 * only mean something if there's one place that defines them.
 *
 * Deliberately icon-free.  The rail draws icons, the Aperture Bar is
 * text-only, and `NovaIcon` drags the whole Nova character kit in
 * with it — putting icons here would pull that kit into the public
 * marketing bundle (which mounts `AppApertureBar`).  The rail keeps
 * its own id → icon map locally.
 *
 * UX-1 — destination 5 (id `business`) is labelled "Galleries" now.
 * A live audit couldn't find Galleries without typing the URL: the
 * product's headline noun was filed under the abstract "Business".
 * The tab always landed on /galleries anyway, so the label now says
 * so.  Same for id `work` → label "Projects".  IDs never change
 * (stored preferences key on them — see NavDestination.id), only the
 * words on the tab.  And per Rennan: the label is NEVER "Studio".
 *
 * BRAND-2 — this file is ALSO the one route → accent registry.
 * Rennan's direction, verbatim: "keep blue primary but let each
 * section wear its color."  Every consumer of a section accent
 * (the `data-orbit-accent` attribute `useTabAccent` writes, the
 * Aperture Bar / rail active tinting, `.section-tint`,
 * `.orbit-eyebrow`) resolves through `sectionAccentForNavId` below
 * so nav and pages can never disagree about what colour a feature
 * wears.
 */

/**
 * BRAND-2 — the section-accent names.  Each maps 1:1 onto a
 * BRAND-1 token pair (`--orbit-<name>` readable tone +
 * `--orbit-<name>-fill` chip fill — see docs/orbit-palette.md).
 *
 * RESTRAINT RULES — read before adding a consumer:
 *
 *   1. Accent ≠ wallpaper.  A section accent tints INTERACTIVE
 *      elements and typographic accents (eyebrows, CTAs, focus
 *      rings, empty-state icons).  It never paints card
 *      backgrounds, large fills, or the frosted dark glass chrome
 *      — those stay neutral.
 *   2. Galleries is deliberately quiet: Hull Silver on controls
 *      only, ZERO accent anywhere near displayed photographs.  An
 *      accent that competes with a client's photos is a defect.
 *   3. Destructive stays Range Red everywhere, regardless of
 *      section.  No accent ever replaces `--destructive`.
 *   4. Consume accents via the theme-aware `--orbit-*` tokens
 *      (`--section-accent` resolves through them), never raw
 *      hexes/HSL — that's how light mode gets the darker legible
 *      tone automatically.  The two-token system IS the contrast
 *      guarantee.
 *   5. Cross-feature/global actions (settings saves, dialog
 *      confirms, auth) keep Mission Blue.  Only surfaces INSIDE a
 *      feature wear the feature's colour.
 */
export type SectionAccent =
  | 'blue'      // Mission Blue    — Today, global chrome
  | 'cyan'      // Blueprint Cyan  — Nova / assistant surfaces
  | 'rose'      // Aurora Rose     — clients, inquiries, leads
  | 'violet'    // Plasma Violet   — Work: projects, calendar, booking
  | 'teal'      // Instrument Teal — sites, emails
  | 'silver'    // Hull Silver     — galleries (quiet next to photos)
  | 'phosphor'  // Phosphor Green  — Money
  | 'gold';     // Kapton Gold     — Passport, goals

/**
 * BRAND-2 — destination/identity id → section accent.  Keys cover
 * BOTH nav vocabularies: the simplified six (`work`, `business`,
 * `money`, …) and the classic/Aperture identity ids that
 * `useTabAccent`'s route map and the classic fourteen-tab list use
 * (`projects`, `expenses`, `sites`, …).  One map so the chrome pill,
 * the ambient, and the page content always agree.
 *
 * Work wears ONE accent (violet) across projects + calendar +
 * booking; calendar EVENT chips keep their per-event colours.
 * Business splits on purpose: galleries goes near-neutral silver
 * (rule 2 above), sites/emails wear teal.  Settings and admin are
 * global chrome — Mission Blue (rule 5).
 */
const SECTION_ACCENTS: Record<string, SectionAccent> = {
  today: 'blue',
  chat: 'cyan',
  clients: 'rose',
  inquiries: 'rose',
  leads: 'rose',
  work: 'violet',
  projects: 'violet',
  calendar: 'violet',
  booking: 'violet',
  business: 'silver',   // the tab lands on /galleries
  galleries: 'silver',
  sites: 'teal',
  emails: 'teal',
  money: 'phosphor',
  expenses: 'phosphor',
  passport: 'gold',
  settings: 'blue',
  admin: 'blue',
};

/**
 * Resolve a nav/destination id (including `-sub` sub-route ids like
 * `galleries-sub`) to its section accent.  Unknown ids fall back to
 * Mission Blue — a surprise route reads as brand, never as broken.
 */
export function sectionAccentForNavId(id: string): SectionAccent {
  const base = id.endsWith('-sub') ? id.slice(0, -4) : id;
  return SECTION_ACCENTS[base] ?? 'blue';
}

export interface NavSubDestination {
  id: string;
  label: string;
  href: string;
}

export interface NavDestination {
  /** Stable key.  This is what gets written to the studio's stored
   *  preference, so renaming one silently drops it from every
   *  existing studio's nav (the healer treats unknown ids as
   *  retired).  Add new ids; don't rename old ones. */
  id: string;
  label: string;
  href: string;
  /** Sub-routes filed under this destination.  Any sub match lights
   *  the parent, and the active parent renders the sub-pill row. */
  subs?: NavSubDestination[];
  /**
   * CHROME-1 — can the studio hide this one?
   *
   * `true` means the toggle is locked on.  Two destinations are
   * locked, for two different reasons:
   *
   *   • today — `/` is the app's home route and the one guaranteed
   *     escape hatch.  Every "back to safety" affordance in the app
   *     lands here.  If a studio could hide Today, a deep link into
   *     a route with no other nav entry (a gallery detail, say)
   *     would leave them with no way out but the browser chrome.
   *
   *   • nova — `/assistant` has no other entry point in the app
   *     shell.  Wave 10 removed the floating Nova companion, so the
   *     nav tab IS the door.  Hiding it wouldn't be a preference,
   *     it would be uninstalling the product's centrepiece.
   *
   * Settings is not on this list at all — it's pinned outside the
   * customisable set (gear beside the Aperture pill, footer row in
   * the rail), so it can't be removed by construction.  That pairing
   * — Today locked in the list, Settings pinned outside it — is what
   * makes stranding impossible no matter what the studio turns off.
   */
  required?: boolean;
}

export const NAV_DESTINATIONS: NavDestination[] = [
  { id: 'today', label: 'Today', href: '/', required: true },
  { id: 'chat', label: 'Nova', href: '/assistant', required: true },
  {
    id: 'clients',
    label: 'Clients',
    href: '/clients',
    subs: [
      { id: 'clients-sub', label: 'Clients', href: '/clients' },
      { id: 'leads-sub', label: 'Leads', href: '/inquiries' },
    ],
  },
  {
    // UX-1 — label was "Work".  The tab lands on /projects and its
    // biggest sub IS Projects, so the tab says the literal noun.
    // The id stays `work` so stored nav preferences keep working.
    id: 'work',
    label: 'Projects',
    href: '/projects',
    subs: [
      { id: 'projects-sub', label: 'Projects', href: '/projects' },
      { id: 'calendar-sub', label: 'Calendar', href: '/calendar' },
      { id: 'booking-sub', label: 'Booking', href: '/settings?tab=scheduling' },
    ],
  },
  {
    // UX-1 — label was "Business", which buried the product's
    // headline noun one abstraction deep.  The tab has always landed
    // on /galleries; now it's called what it opens.  Sites and
    // Emails stay one hop away as subs, same as before.  The id
    // stays `business` so stored nav preferences keep working.
    id: 'business',
    label: 'Galleries',
    href: '/galleries',
    subs: [
      { id: 'galleries-sub', label: 'Galleries', href: '/galleries' },
      { id: 'sites-sub', label: 'Sites', href: '/sites' },
      { id: 'emails-sub', label: 'Emails', href: '/emails' },
    ],
  },
  { id: 'money', label: 'Money', href: '/expenses' },
];

/** Canonical order, used by the "start over" affordance and by the
 *  healer when it appends a destination that shipped after the
 *  studio last touched their preference. */
export const NAV_DESTINATION_IDS: string[] = NAV_DESTINATIONS.map((d) => d.id);

export function navDestinationById(id: string): NavDestination | undefined {
  return NAV_DESTINATIONS.find((d) => d.id === id);
}

/** One-line description of what each destination holds.  Used by the
 *  Settings editor and the onboarding picker so the studio isn't
 *  toggling bare words. */
export const NAV_DESTINATION_BLURB: Record<string, string> = {
  today: 'Your day, your money, your next move.',
  chat: 'Ask for anything. Nova does the work.',
  clients: 'Everyone you work with, and everyone who wants to.',
  work: 'Your shoots, your calendar, and your booking page.',
  business: 'Client galleries, your site, and the emails you send.',
  money: 'What came in, what went out.',
};
